/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Download, CheckCircle2, XCircle, AlertTriangle, Zap } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { DOC_TYPES, labelFor, relativeTime, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

export default function FirmDocuments() {
    const [submissions, setSubmissions] = useState([]);
    const [clients, setClients] = useState([]);
    const [status, setStatus] = useState("pending_review");
    const [docType, setDocType] = useState("all");
    const [clientId, setClientId] = useState("all");
    const [loading, setLoading] = useState(true);
    const [review, setReview] = useState(null);
    // Bulk-select state — Set<submission_id>. Cleared on filter change so
    // stale selections never carry into a different view.
    const [selected, setSelected] = useState(new Set());
    const [bulkAction, setBulkAction] = useState(null); // "accepted" | "needs_more_info" | "rejected"
    const [autoApprove, setAutoApprove] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (status !== "all") params.status = status;
            if (docType !== "all") params.document_type = docType;
            if (clientId !== "all") params.client_id = clientId;
            const { data } = await api.get("/documents/", { params });
            setSubmissions(data);
            setSelected(new Set());   // drop selections after any reload
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { api.get("/clients/").then((r) => setClients(r.data)).catch(() => {}); }, []);
    useEffect(() => {
        api.get("/tenant/").then((r) => setAutoApprove(!!r.data?.submissions_auto_approve)).catch(() => {});
    }, []);
    useEffect(() => { load(); /* @co eslint set-state-in-effect ok */ }, [status, docType, clientId]);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    const allChecked = submissions.length > 0 && selected.size === submissions.length;
    const someChecked = selected.size > 0 && !allChecked;

    function toggleOne(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }
    function toggleAll() {
        setSelected(allChecked ? new Set() : new Set(submissions.map((s) => s.id)));
    }

    async function saveAutoApprove(next) {
        // Optimistic — flip the toggle immediately, revert on failure.
        setAutoApprove(next);
        try {
            await api.patch("/tenant/", { submissions_auto_approve: next });
            toast.success(next
                ? "Auto-approve ON — new submissions will be accepted automatically"
                : "Auto-approve OFF — new submissions will land in Pending review");
        } catch (e) {
            setAutoApprove(!next);
            toast.error(formatApiError(e));
        }
    }

    async function downloadFile(submissionId, idx, name) {
        try {
            const { data } = await api.get(`/documents/${submissionId}/file/${idx}`);
            const a = document.createElement("a");
            a.href = `data:${data.mime};base64,${data.data_base64}`;
            a.download = data.name || name || "download";
            a.click();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Documents"
                title="Submissions review"
                description="Every uploaded document — categorised by type, period and client. Review, accept, or ask for more info without ever switching apps."
                testId="documents-header"
            />

            <AutoApproveCard value={autoApprove} onChange={saveAutoApprove} />

            <div className="co-card co-card-pad mt-4 flex flex-wrap gap-3 items-end">
                <Filter label="Status" value={status} onChange={setStatus} testId="docs-filter-status">
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending_review">Pending review</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="needs_more_info">Needs more info</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                </Filter>
                <Filter label="Document type" value={docType} onChange={setDocType} testId="docs-filter-type">
                    <SelectItem value="all">All types</SelectItem>
                    {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </Filter>
                <Filter label="Client" value={clientId} onChange={setClientId} testId="docs-filter-client">
                    <SelectItem value="all">All clients</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </Filter>
            </div>

            {selected.size > 0 && (
                <BulkActionBar
                    count={selected.size}
                    onAccept={() => setBulkAction("accepted")}
                    onNeedsInfo={() => setBulkAction("needs_more_info")}
                    onReject={() => setBulkAction("rejected")}
                    onClear={() => setSelected(new Set())}
                />
            )}

            <div className="mt-6">
                {loading ? (
                    <div className="text-sm text-[#5E636E]">Loading…</div>
                ) : submissions.length === 0 ? (
                    <EmptyState icon={FileText} title="No submissions match" description="Adjust filters or ask a client to upload via their portal." testId="docs-empty" />
                ) : (
                    <div className="co-card overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-2 border-b border-[#E4E4E7] bg-[#FAFAFA]">
                            <label className="flex items-center gap-2 text-xs text-[#5E636E] cursor-pointer" data-testid="docs-select-all-label">
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                    onChange={toggleAll}
                                    className="h-4 w-4 rounded border-[#A4A6AB] text-[#0E3B2E] focus:ring-[#0E3B2E]"
                                    data-testid="docs-select-all"
                                />
                                Select all ({submissions.length})
                            </label>
                        </div>
                        <ul className="divide-y divide-[#E4E4E7]" data-testid="docs-list">
                            {submissions.map((s) => {
                                const checked = selected.has(s.id);
                                return (
                                <li
                                    key={s.id}
                                    className={`p-4 flex flex-col sm:flex-row sm:items-center gap-3 transition-colors ${checked ? "bg-[#F5F4EF]" : ""}`}
                                    data-testid={`doc-row-${s.id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleOne(s.id)}
                                        className="h-4 w-4 shrink-0 rounded border-[#A4A6AB] text-[#0E3B2E] focus:ring-[#0E3B2E] mt-1 sm:mt-0"
                                        data-testid={`doc-row-check-${s.id}`}
                                        aria-label={`Select submission ${s.id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="text-sm font-medium">{s.category_label || labelFor(DOC_TYPES, s.document_type)}</div>
                                            <StatusBadge status={s.status} />
                                            {s.financial_year && <span className="inline-flex items-center rounded-full border border-[#E4E4E7] px-1.5 py-0.5 text-[10px] text-[#5E636E]">FY{s.financial_year}</span>}
                                            {s.period_month && <span className="inline-flex items-center rounded-full border border-[#E4E4E7] px-1.5 py-0.5 text-[10px] text-[#5E636E]">{s.period_month}</span>}
                                            {s.source_channel && s.source_channel !== "portal" && <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800">via {s.source_channel}</span>}
                                        </div>
                                        <div className="mt-1 text-xs text-[#5E636E]">
                                            {clientName(s.client_id)} · {s.files?.length || 0} file{(s.files?.length || 0) === 1 ? "" : "s"}
                                            {s.period_start && ` · ${formatDate(s.period_start)} → ${formatDate(s.period_end)}`}
                                            <span className="mx-1">·</span>
                                            {relativeTime(s.created_at)}
                                        </div>
                                        {s.notes && <div className="mt-1 text-xs text-[#0F1115] line-clamp-2">{s.notes}</div>}
                                        {s.files?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {s.files.map((f, i) => (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => downloadFile(s.id, i, f.name)}
                                                        className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1 text-[11px] font-mono hover:bg-zinc-50"
                                                        data-testid={`doc-file-${s.id}-${i}`}
                                                    >
                                                        <Download className="h-3 w-3" /> {f.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" onClick={() => setReview(s)} data-testid={`doc-review-${s.id}`}>Review</Button>
                                    </div>
                                </li>
                            );})}
                        </ul>
                    </div>
                )}
            </div>

            <ReviewDialog
                submission={review}
                onClose={() => setReview(null)}
                onDone={async () => { setReview(null); await load(); }}
            />
            <BulkReviewDialog
                action={bulkAction}
                ids={Array.from(selected)}
                onClose={() => setBulkAction(null)}
                onDone={async () => { setBulkAction(null); await load(); }}
            />
        </div>
    );
}


function AutoApproveCard({ value, onChange }) {
    return (
        <div className="co-card mt-4 p-4 flex items-start gap-3" data-testid="auto-approve-card">
            <div className="grid h-9 w-9 place-items-center rounded-full bg-[#F5F4EF] text-[#0E3B2E] shrink-0">
                <Zap className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">Auto-approve new submissions</span>
                    <Switch
                        checked={value}
                        onCheckedChange={onChange}
                        data-testid="auto-approve-toggle"
                        aria-label="Auto-approve submissions"
                    />
                </div>
                <p className="text-xs text-[#5E636E] mt-1 leading-relaxed">
                    {value ? (
                        <>
                            <span className="font-medium text-emerald-800">ON.</span> New portal uploads and emailed documents are accepted automatically — they skip the Pending review queue. Existing pending items stay pending.
                        </>
                    ) : (
                        <>
                            <span className="font-medium text-[#0F1115]">OFF.</span> New submissions land in Pending review for a human decision (recommended for firms who want full oversight).
                        </>
                    )}
                </p>
            </div>
        </div>
    );
}


function BulkActionBar({ count, onAccept, onNeedsInfo, onReject, onClear }) {
    return (
        <div className="sticky top-2 z-10 mt-4 co-card co-card-pad flex flex-wrap items-center justify-between gap-3 border-2 border-[#0E3B2E]/40 bg-white shadow-md" data-testid="bulk-action-bar">
            <div className="text-sm">
                <span className="font-medium text-[#0F1115]" data-testid="bulk-count">{count}</span>
                <span className="text-[#5E636E]"> selected · Bulk review</span>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={onNeedsInfo} data-testid="bulk-needs-info">
                    <AlertTriangle className="h-3.5 w-3.5" /> Needs info
                </Button>
                <Button size="sm" variant="outline" onClick={onReject} data-testid="bulk-reject">
                    <XCircle className="h-3.5 w-3.5" /> Reject
                </Button>
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onAccept} data-testid="bulk-accept">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Accept all
                </Button>
                <Button size="sm" variant="ghost" onClick={onClear} data-testid="bulk-clear">Clear</Button>
            </div>
        </div>
    );
}


function BulkReviewDialog({ action, ids, onClose, onDone }) {
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (action) setNotes(""); }, [action]);

    if (!action) return null;
    const label = action === "accepted" ? "Accept" : action === "needs_more_info" ? "Ask for more info on" : "Reject";
    const tone = action === "accepted" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-[#171717] hover:bg-[#2E2E2E]";

    async function confirm() {
        setBusy(true);
        try {
            const { data } = await api.post("/documents/bulk-review", {
                submission_ids: ids,
                status: action,
                review_notes: notes || null,
            });
            const parts = [`${data.updated} of ${data.requested} updated`];
            if (data.failed) parts.push(`${data.failed} failed`);
            toast.success(parts.join(" · "));
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={!!action} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent data-testid="bulk-review-dialog">
                <DialogHeader>
                    <DialogTitle>{label} {ids.length} submission{ids.length === 1 ? "" : "s"}?</DialogTitle>
                    <DialogDescription>
                        This will apply the same decision to all selected submissions and close any linked review tasks.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label>Optional note back to clients</Label>
                    <Textarea
                        rows={3}
                        className="mt-1"
                        placeholder={action === "needs_more_info" ? "e.g. Please resend a clearer scan" : "Add context — this shows on the client portal"}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid="bulk-notes"
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="bulk-cancel">Cancel</Button>
                    <Button onClick={confirm} disabled={busy} className={`${tone} text-white`} data-testid="bulk-confirm">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `${label} ${ids.length}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Filter({ label, value, onChange, children, testId }) {
    return (
        <div>
            <Label className="text-[11px] text-[#5E636E]">{label}</Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="mt-1 min-w-[180px]" data-testid={testId}><SelectValue /></SelectTrigger>
                <SelectContent>{children}</SelectContent>
            </Select>
        </div>
    );
}

function ReviewDialog({ submission, onClose, onDone }) {
    const [status, setStatus] = useState("accepted");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (submission) {
            setStatus("accepted");
            setNotes(submission.review_notes || "");
        }
    }, [submission]);

    async function submit() {
        if (!submission) return;
        setBusy(true);
        try {
            await api.post(`/documents/${submission.id}/review`, { status, review_notes: notes });
            toast.success(`Submission marked ${status.replace(/_/g, " ")}`);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={!!submission} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent data-testid="review-dialog">
                <DialogHeader><DialogTitle>Review submission</DialogTitle></DialogHeader>
                {submission && (
                    <div className="space-y-3">
                        <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3 text-xs space-y-1">
                            <div><span className="text-[#5E636E]">Type: </span>{labelFor(DOC_TYPES, submission.document_type)}</div>
                            <div><span className="text-[#5E636E]">Files: </span>{submission.files?.length || 0}</div>
                            {submission.notes && <div><span className="text-[#5E636E]">Client note: </span>{submission.notes}</div>}
                        </div>
                        <div>
                            <Label>Decision</Label>
                            <div className="mt-2 grid grid-cols-3 gap-2">
                                <DecisionPill icon={CheckCircle2} label="Accept" v="accepted" current={status} onClick={() => setStatus("accepted")} />
                                <DecisionPill icon={AlertTriangle} label="Needs info" v="needs_more_info" current={status} onClick={() => setStatus("needs_more_info")} />
                                <DecisionPill icon={XCircle} label="Reject" v="rejected" current={status} onClick={() => setStatus("rejected")} />
                            </div>
                        </div>
                        <div>
                            <Label>Review notes</Label>
                            <Textarea rows={3} className="mt-1" placeholder="Optional note back to the client." value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="review-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="review-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save review"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DecisionPill({ icon: Icon, label, v, current, onClick }) {
    const active = current === v;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`review-decision-${v}`}
            className={`inline-flex flex-col items-center gap-1 rounded-md border px-3 py-3 text-xs transition-colors ${
                active ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"
            }`}
        >
            <Icon className="h-4 w-4" />
            {label}
        </button>
    );
}
